import React, { useEffect, useState } from 'react';
import { BellRing, Loader2, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { formatApiError } from '@/utils/apiError';

const LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null);

export function PostcodeNudgeButton({ onSent }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/api/staff/postcode-nudge/candidates`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d));
      setData(d);
    } catch (e) { toast.error(e.message || 'Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/postcode-nudge/send`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d));
      toast.success(`Nudged ${d.sent} installer${d.sent === 1 ? '' : 's'} · ${d.sms} text${d.sms === 1 ? '' : 's'}, ${d.email} email${d.email === 1 ? '' : 's'}`);
      setOpen(false);
      await load();
      onSent?.();
    } catch (e) { toast.error(e.message || 'Send failed'); } finally { setBusy(false); }
  };

  if (!data || data.candidates.length === 0) return null;
  const rows = data.candidates;

  return (
    <>
      <Button size="sm" variant="outline" className="border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100" onClick={() => setOpen(true)} data-testid="map-nudge-open">
        <BellRing className="w-3.5 h-3.5 mr-1" /> Nudge {rows.length} approved without postcode
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="map-nudge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BellRing className="w-5 h-5 text-amber-600" /> Postcode nudge</DialogTitle>
            <DialogDescription>
              Texts and emails these Ox Approved installers asking them to add a postcode so they can be matched to nearby leads. Each installer is nudged at most once every {data.cooldown_days} days. Wording is editable in the Message Centre (<em>Postcode nudge</em>).
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-72 overflow-y-auto divide-y border rounded-lg" data-testid="map-nudge-list">
            {rows.map((r) => (
              <li key={r.user_id} className={`px-3 py-2 text-sm flex items-center gap-2 ${r.eligible ? '' : 'opacity-50'}`} data-testid={`map-nudge-row-${r.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.company_name || r.name}</div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                    <span>{r.approved.map((s) => LABEL[s] || s).join(', ')}</span>
                    {r.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span> : <span className="text-red-500">no mobile</span>}
                    {r.email ? <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span> : <span className="text-red-500">no email</span>}
                  </div>
                </div>
                <span className="text-[11px] font-semibold shrink-0">{r.eligible ? <span className="text-emerald-700">Will send</span> : <span className="text-gray-500">Nudged {fmt(r.last_nudged_at)}</span>}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="map-nudge-cancel">Cancel</Button>
            <Button onClick={send} disabled={busy || data.eligible === 0} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="map-nudge-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <BellRing className="w-4 h-4 mr-1" />} Send to {data.eligible}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
